/* Delivery and Pickup Selection Component, matching the homepage design system.
   - Delivery: 2-month calendar where the customer picks ONE delivery day. Only Monday-Friday,
     Dutch public holidays excluded, earliest day based on the admin lead-time setting
     (working days after ordering, orders after the cutoff hour count from the next working day).
   - Pickup: 2-month calendar with available dates and hourly time slots from 9:00-16:00.
   Pickup can be disabled for carts containing products that require transport. */

import { useState, useMemo, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Truck, Store, Check, Clock } from 'lucide-react';
import { addDays, isBefore, format } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  DEFAULT_LEAD_TIME, fetchDeliveryLeadTime, getEarliestDeliveryDate, isSelectableDeliveryDate,
  type DeliveryLeadTime,
} from '@/lib/delivery-date';

interface DeliverySelectorProps {
  deliveryType: 'delivery' | 'pickup' | null;
  deliveryWeek: string;
  pickupTimeslot: string;
  onDeliveryTypeChange: (type: 'delivery' | 'pickup') => void;
  onDeliveryWeekChange: (week: string) => void;
  onPickupTimeslotChange: (timeslot: string) => void;
  allowPickup?: boolean;
  addressComplete?: boolean;
}

const TIME_SLOTS = [
  '09:00-10:00', '10:00-11:00', '11:00-12:00',
  '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
];

export default function DeliverySelector({
  deliveryType, deliveryWeek, pickupTimeslot,
  onDeliveryTypeChange, onDeliveryWeekChange, onPickupTimeslotChange,
  allowPickup = true, addressComplete = false,
}: DeliverySelectorProps) {
  const [selectedPickupDate, setSelectedPickupDate] = useState<string>('');
  const [leadTime, setLeadTime] = useState<DeliveryLeadTime>(DEFAULT_LEAD_TIME);

  useEffect(() => {
    let cancelled = false;
    fetchDeliveryLeadTime().then((lt) => { if (!cancelled) setLeadTime(lt); });
    return () => { cancelled = true; };
  }, []);

  const earliestDelivery = useMemo(() => getEarliestDeliveryDate(leadTime), [leadTime]);

  useEffect(() => {
    if (deliveryWeek && !isSelectableDeliveryDate(new Date(`${deliveryWeek}T00:00:00`), earliestDelivery)) {
      onDeliveryWeekChange('');
    }
  }, [deliveryWeek, earliestDelivery, onDeliveryWeekChange]);

  const getAvailablePickupDates = () => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let daysChecked = 0;
    let datePointer = new Date(today);
    datePointer.setDate(datePointer.getDate() + 3);
    while (dates.length < 14) {
      const dayOfWeek = datePointer.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5 || dayOfWeek === 6) {
        const weekStart = new Date(datePointer);
        weekStart.setDate(weekStart.getDate() - (weekStart.getDay() || 7) + 1);
        weekStart.setHours(0, 0, 0, 0);
        const nextWeekStart = new Date(today);
        nextWeekStart.setDate(nextWeekStart.getDate() + (7 - today.getDay() + 1));
        nextWeekStart.setHours(0, 0, 0, 0);
        if (weekStart >= nextWeekStart) dates.push(new Date(datePointer));
      }
      datePointer.setDate(datePointer.getDate() + 1);
      daysChecked++;
      if (daysChecked > 30) break;
    }
    return dates;
  };

  const availablePickupDates = useMemo(() => getAvailablePickupDates(), []);

  const selectedDeliveryDate = deliveryWeek ? new Date(`${deliveryWeek}T00:00:00`) : undefined;

  const handlePickupSelection = (date: string, time: string) => {
    setSelectedPickupDate(date);
    onPickupTimeslotChange(`${date} ${time}`);
  };

  return (
    <div className="rounded-xl bg-white p-6 ring-1 ring-[#e7e1d6] transition-[transform,box-shadow] duration-300 hover:shadow-[0_14px_30px_rgba(30,43,34,0.08)] sm:p-8">
      <h2 className="text-2xl tracking-[-0.035em] text-[#1e2b22]">Levering</h2>

      {/* Delivery / Pickup toggle */}
      <div className="mt-5 space-y-3">
        {allowPickup && (
          <button type="button" onClick={() => onDeliveryTypeChange('pickup')}
            className={`flex w-full items-center gap-3 rounded-xl p-4 text-left ring-1 transition-all ${
              deliveryType === 'pickup' ? 'bg-[#1e2b22]/5 ring-[#1e2b22]' : 'bg-[#fcfaf6] ring-[#e7e1d6] hover:ring-[#5a6552] hover:bg-[#cfcac2]/40'
            }`}>
            <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ${deliveryType === 'pickup' ? 'bg-[#1e2b22] ring-[#1e2b22]' : 'ring-[#cfcac2]'}`}>
              {deliveryType === 'pickup' && <div className="h-2 w-2 rounded-full bg-white" />}
            </div>
            <Store className="h-5 w-5 text-[#5a6552]" />
            <div>
              <p className="text-sm font-semibold text-[#1e2b22]">Afhalen in Dedemsvaart</p>
              <p className="text-xs text-[#8b867d]">Gratis, haal je bestelling op bij de kwekerij</p>
              <p className="mt-1 text-xs font-medium text-[#1e2b22]">20% korting met code AFHALEN20 (alleen Kwekerij-producten)</p>
            </div>
          </button>
        )}

        <button type="button" onClick={() => addressComplete && onDeliveryTypeChange('delivery')} disabled={!addressComplete}
          className={`flex w-full items-center gap-3 rounded-xl p-4 text-left ring-1 transition-all ${
            deliveryType === 'delivery' ? 'bg-[#1e2b22]/5 ring-[#1e2b22]' : addressComplete ? 'bg-[#fcfaf6] ring-[#e7e1d6] hover:ring-[#5a6552] hover:bg-[#cfcac2]/40' : 'cursor-not-allowed bg-[#fcfaf6] ring-[#e7e1d6] opacity-50'
          }`}>
          <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ${deliveryType === 'delivery' ? 'bg-[#1e2b22] ring-[#1e2b22]' : 'ring-[#cfcac2]'}`}>
            {deliveryType === 'delivery' && <div className="h-2 w-2 rounded-full bg-white" />}
          </div>
          <Truck className="h-5 w-5 text-[#5a6552]" />
          <div>
            <p className="text-sm font-semibold text-[#1e2b22]">
              Bezorging{!addressComplete && <span className="font-normal text-[#8b867d]">, selecteer eerst een adres</span>}
            </p>
            <p className="text-xs text-[#8b867d]">Wij bezorgen je bestelling thuis</p>
          </div>
        </button>
      </div>

      {/* Delivery calendar */}
      {deliveryType === 'delivery' && (
        <div className="mt-6">
          {!addressComplete ? (
            <div className="rounded-xl bg-[#fcfaf6] p-4 ring-1 ring-[#e7e1d6]">
              <div className="flex items-start gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#5a6552]"><Truck className="h-4 w-4" /></div>
                <p className="pt-1.5 text-sm leading-5 text-[#1e2b22]">Vul eerst je adres in, dan kun je de levering selecteren.</p>
              </div>
            </div>
          ) : (
            <>
              <h3 className="text-base font-semibold text-[#1e2b22]">Kies je bezorgdag</h3>
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-[#fcfaf6] px-3 py-2.5 ring-1 ring-[#e7e1d6]">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#5a6552]" />
                <p className="text-sm leading-5 text-[#5a6552]">
                  Besteld voor {leadTime.cutoff_hour}:00 uur? Dan bezorgen we op z'n vroegst op{' '}
                  <span className="font-semibold text-[#1e2b22]">{format(earliestDelivery, 'EEEE d MMMM', { locale: nl })}</span>.
                  We bezorgen op werkdagen (maandag t/m vrijdag), niet op feestdagen.
                </p>
              </div>
              <div className="mt-3 rounded-xl bg-[#fcfaf6] p-2 ring-1 ring-[#e7e1d6] sm:p-4" style={{ maxWidth: '100%' }}>
                <Calendar
                  mode="single" numberOfMonths={2} showWeekNumber={false} weekStartsOn={1}
                  locale={nl}
                  defaultMonth={earliestDelivery}
                  selected={selectedDeliveryDate}
                  onSelect={(date) => { if (date) onDeliveryWeekChange(format(date, 'yyyy-MM-dd')); }}
                  disabled={(date) => !isSelectableDeliveryDate(date, earliestDelivery)}
                  modifiersClassNames={{
                    selected: 'bg-[#1e2b22] text-white hover:bg-[#1e2b22] hover:text-white font-bold',
                  }}
                  className="w-full"
                  classNames={{
                    months: 'flex flex-col gap-4 sm:flex-row w-full',
                    month: 'flex-1 min-w-0',
                    day: 'h-7 w-7 sm:h-8 sm:w-8 p-0 font-normal aria-selected:opacity-100 text-xs sm:text-sm hover:bg-[#cfcac2] hover:text-[#1e2b22]',
                  }}
                />
                {selectedDeliveryDate && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#5a6552] animate-in fade-in duration-300">
                    <Check className="h-4 w-4 text-[#1e2b22]" />
                    Bezorging op <span className="font-semibold text-[#1e2b22]">{format(selectedDeliveryDate, 'EEEE d MMMM yyyy', { locale: nl })}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Pickup calendar */}
      {allowPickup && deliveryType === 'pickup' && (
        <div className="mt-6">
          <h3 className="text-base font-semibold text-[#1e2b22]">Selecteer datum</h3>
          <div className="mt-3 rounded-xl bg-[#fcfaf6] p-2 ring-1 ring-[#e7e1d6] sm:p-4" style={{ maxWidth: '100%' }}>
            <Calendar
              mode="single" numberOfMonths={2} showWeekNumber={false} weekStartsOn={1}
              locale={nl}
              selected={selectedPickupDate ? new Date(selectedPickupDate) : undefined}
              onSelect={(date) => { if (date) setSelectedPickupDate(format(date, 'yyyy-MM-dd')); }}
              disabled={(date) => {
                const dayOfWeek = date.getDay();
                if (dayOfWeek === 0) return true;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const minDate = addDays(today, 3);
                const nextWeekStart = new Date(today);
                nextWeekStart.setDate(nextWeekStart.getDate() + (7 - today.getDay() + 1));
                nextWeekStart.setHours(0, 0, 0, 0);
                const dateWeekStart = new Date(date);
                dateWeekStart.setDate(dateWeekStart.getDate() - (dateWeekStart.getDay() || 7) + 1);
                dateWeekStart.setHours(0, 0, 0, 0);
                return isBefore(date, minDate) || isBefore(dateWeekStart, nextWeekStart);
              }}
              modifiersClassNames={{ selected: 'bg-[#1e2b22] text-white hover:bg-[#1e2b22] hover:text-white' }}
              className="w-full"
              classNames={{
                months: 'flex flex-col gap-4 sm:flex-row w-full',
                month: 'flex-1 min-w-0',
                day: 'h-7 w-7 sm:h-8 sm:w-8 p-0 font-normal aria-selected:opacity-100 text-xs sm:text-sm hover:bg-[#cfcac2] hover:text-[#1e2b22]',
              }}
            />
            {selectedPickupDate && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#5a6552]">
                <Check className="h-4 w-4 text-[#1e2b22]" />
                Geselecteerd: {new Date(selectedPickupDate).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }).toLowerCase()}
              </div>
            )}
          </div>

          {selectedPickupDate && (
            <div className="mt-6">
              <h3 className="text-base font-semibold text-[#1e2b22]">Selecteer tijdstip</h3>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {TIME_SLOTS.map((slot) => {
                  const isSelected = pickupTimeslot.split(' ').slice(1).join(' ') === slot;
                  return (
                    <button key={slot} type="button" onClick={() => handlePickupSelection(selectedPickupDate, slot)}
                      className={`rounded-lg px-3 py-2.5 text-sm font-medium ring-1 transition-all ${
                        isSelected ? 'bg-[#1e2b22] text-white ring-[#1e2b22]' : 'bg-[#fcfaf6] text-[#1e2b22] ring-[#e7e1d6] hover:ring-[#5a6552]'
                      }`}>
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
